/**
 * RevokeLinkDialog Component (Story 14.2)
 *
 * Confirmation dialog for soft-revoking a portal link.
 */

import { useMutation } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { Trash } from "@phosphor-icons/react";
import {
  revokePortalLink,
  type PortalLinkProjection,
} from "../../lib/portalLinksApi";
import type { ToastType } from "../../hooks/useToast";

interface RevokeLinkDialogProps {
  link: PortalLinkProjection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRevoked: () => void;
  showToast: (type: ToastType, message: string) => void;
}

export function RevokeLinkDialog({
  link,
  open,
  onOpenChange,
  onRevoked,
  showToast,
}: RevokeLinkDialogProps) {
  const revoke = useMutation({
    mutationFn: () => revokePortalLink(link.id),
    onSuccess: () => onRevoked(),
    onError: (err: Error) => {
      showToast("error", err.message ?? "Failed to revoke link");
    },
  });

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 bg-black/40 z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-background p-8 shadow-lg"
                style={{ border: "0.5px solid var(--hairline)" }}
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <DialogPrimitive.Title className="text-lg font-semibold">
                  Revoke share link
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-[13px] text-muted-foreground mt-2">
                  This will immediately disable “{link.label}”. Anyone with the
                  URL will see “This link is no longer active”. This cannot be
                  undone.
                </DialogPrimitive.Description>
                <div className="flex justify-end gap-3 mt-6">
                  <DialogPrimitive.Close asChild>
                    <Button
                      variant="outline"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      disabled={revoke.isPending}
                    >
                      Cancel
                    </Button>
                  </DialogPrimitive.Close>
                  <Button
                    variant="destructive"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={() => revoke.mutate()}
                    disabled={revoke.isPending}
                    data-testid="confirm-revoke"
                  >
                    <Trash size={14} className="mr-1" />
                    {revoke.isPending ? "Revoking…" : "Revoke link"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
